import type {useSortable} from '@dnd-kit/sortable';
import {atoms, Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {DragHandleLine} from '@dropbox/dig-icons/assets';
import {useState} from 'react';

type Sortable = ReturnType<typeof useSortable>;

// Hung off the card's left edge, like the detail page's back arrow off the
// title: the 16px icon plus an 8px gap, level with the card's status.
const GUTTER_PX = 24;
const HANDLE_TOP_PX = 20;
// The handle's target pads the icon to 24px tall, so it starts above it.
const HANDLE_PAD_Y_PX = 4;

/**
 * /workstreams: a sortable card's drag handle. Every card's shows
 * (`shown`) while the pointer is anywhere level with the list, faint; the one
 * for the row the pointer is level with (`active`), the one being dragged, or
 * one holding keyboard focus (Space / Enter then picks the card up and the
 * arrows move it) in base text. It sits in a gutter the card's full height, so
 * the pointer never leaves the card on its way to the handle; `onHoverChange`
 * reports the pointer in that gutter, so the card can stay lit.
 */
export const SortableHandle = ({
  sortable: {attributes, listeners, setActivatorNodeRef, isDragging},
  shown,
  active,
  onHoverChange,
}: {
  sortable: Pick<Sortable, 'attributes' | 'listeners' | 'setActivatorNodeRef' | 'isDragging'>;
  shown: boolean;
  active: boolean;
  onHoverChange: (hovered: boolean) => void;
}) => {
  const [focused, setFocused] = useState(false);
  const visible = shown || isDragging || focused;
  const emphasized = active || isDragging || focused;
  return (
    <Box
      position="absolute"
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
      style={{left: -GUTTER_PX, top: 0, bottom: 0, width: GUTTER_PX}}
    >
      <Box
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
        display="flex"
        onFocus={(event: React.FocusEvent<HTMLElement>) =>
          setFocused(event.currentTarget.matches(':focus-visible'))
        }
        onBlur={() => setFocused(false)}
        style={{
          marginTop: HANDLE_TOP_PX - HANDLE_PAD_Y_PX,
          paddingTop: HANDLE_PAD_Y_PX,
          paddingBottom: HANDLE_PAD_Y_PX,
          cursor: isDragging ? 'grabbing' : 'grab',
          opacity: visible ? 1 : 0,
          transition: 'opacity 120ms ease',
          touchAction: 'none',
        }}
      >
        <UIIcon
          src={DragHandleLine}
          size="small"
          className={atoms({color: emphasized ? 'Text Base' : 'Text Subtle'})}
        />
      </Box>
    </Box>
  );
};
